"use client";

import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Loader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import {
  AlertCircle,
  Banknote,
  CheckCircle2,
  CircleDollarSign,
  Landmark,
  Plus,
  Receipt,
  Trash2,
  Wallet,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { PosCheckoutPreviewDTO } from "@/modules/merchant/domain/contracts";
import {
  applyExclusivePosTax,
  formatPosTaxPercentLabel,
} from "@/modules/merchant/domain/pos-exclusive-tax";
import type { ShopSellableCatalogRow } from "@/modules/merchant/presentation/api/client";
import {
  usePosCheckoutMutation,
  usePosCheckoutPreviewQuery,
} from "@/modules/merchant/presentation/api/queries";
import { formatPosFxRateLine } from "@/modules/merchant/presentation/lib/pos-fx-labels";
import { posToastError } from "@/modules/merchant/presentation/lib/pos-user-messages";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { ShopPosCheckoutSuccessPanel } from "@/modules/merchant/presentation/ui/shop-pos/ShopPosCheckoutSuccessPanel";
import type { ShopCurrency } from "@/shared/lib/currency";
import {
  formatPrice,
  isShopCurrency,
  posPaymentIsStrictUnderpayment,
  posPaymentMeetsOrExceedsInvoice,
  posPaymentTotalsMatchInvoice,
  SHOP_CURRENCIES,
} from "@/shared/lib/currency";
import {
  convertShopCurrencyAmount,
  suggestPaymentAmountNotOverInvoice,
} from "@/shared/lib/exchange-rate";

/** ລະຫັດແຕ່ລະບັນທັດກະຕ່າ — ໃຊ້ແຍກບັນທັດຊື່ອກັນ (ສ່ວນຫຼຸດຕໍ່ລາຍການຕ່າງກັນ) */
export function newPosLineInstanceId(): string {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export type ShopPosCartLine = {
  row: ShopSellableCatalogRow;
  qty: number;
  lineInstanceId: string;
  lotId?: string;
  lotNo?: string;
  serialId?: string;
  serialNo?: string;
  /** ສ່ວນຫຼຸດຕໍ່ລາຍການ (ເງິນ) */
  lineDiscountAmount?: number;
  /** ສ່ວນຫຼຸດຕໍ່ລາຍການ (%) */
  lineDiscountPercent?: number;
  /** true = ໂຫມດ % (ສະແດງຊ່ອງ % ແມ້ຍັງ 0%) — ຢ່າພຶ່ງ (lineDiscountPercent ?? 0) > 0 ເປັນໂຫມດ */
  lineDiscountUsePercent?: boolean;
};

/** ສະແດງຕົວເລກເງິນໃນຊ່ອງກອກ — ຄັ່ນເປັນ 1,234,567 (.xx ຖ້າມີ) */
function formatMoneyInputDisplay(n: number): string {
  if (!Number.isFinite(n) || n < 0) return "";
  const rounded = Math.round(n * 100) / 100;
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(rounded);
}

/** ອ່ານຄ່າຈາກຊ່ອງທີ່ມີ comma */
function parseMoneyInput(s: string): number {
  const normalized = s.replace(/,/g, "").trim();
  if (normalized === "" || normalized === ".") return 0;
  const n = Number(normalized);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(n * 100) / 100;
}

/** ລະຫວ່າງກົດ — ອະນຸຍາດ 0. , 0.0 ຈົນກວ່າ blur */
function sanitizeMoneyDecimalTyping(raw: string): string {
  const s = raw
    .replace(/,/g, "")
    .trim()
    .replace(/[^\d.]/g, "");
  const firstDot = s.indexOf(".");
  if (firstDot === -1) return s;
  const intPart = s.slice(0, firstDot);
  const after = s.slice(firstDot + 1).replace(/\./g, "");
  return `${intPart}.${after.slice(0, 2)}`;
}

function lineGross(l: ShopPosCartLine): number {
  return l.row.price * l.qty;
}

function lineDiscountApplied(l: ShopPosCartLine): number {
  const g = lineGross(l);
  if (l.lineDiscountUsePercent === true) {
    return Math.min((g * (l.lineDiscountPercent ?? 0)) / 100, g);
  }
  return Math.min(l.lineDiscountAmount ?? 0, g);
}

/** MC4 — ຍອດຫຼັງຫຼຸດຕໍ່ລາຍການ ໃນສະກຸນບິນ (ຄືນ NaN ຖ້າບໍ່ມີອັດຕາ) */
export function cartNetSubtotalInInvoiceCurrency(
  cart: ShopPosCartLine[],
  invoiceCurrency: ShopCurrency,
  rateMap: ReadonlyMap<string, number>,
): number {
  let sum = 0;
  for (const line of cart) {
    const g = lineGross(line);
    const d = lineDiscountApplied(line);
    const net = round2(g - d);
    const from = line.row.currency;
    if (!isShopCurrency(from)) continue;
    const conv = convertShopCurrencyAmount(net, from, invoiceCurrency, rateMap);
    if (conv == null) return Number.NaN;
    sum += conv.convertedAmount;
  }
  return round2(sum);
}

type PosPayMethod = "cash" | "transfer" | "wallet";

const METHOD_CONFIG: Record<
  PosPayMethod,
  { label: string; Icon: typeof Banknote }
> = {
  cash: { label: "ເງິນສົດ", Icon: Banknote },
  transfer: { label: "ໂອນ", Icon: Landmark },
  wallet: { label: "ກະເປົາເງິນ", Icon: Wallet },
};

type PaymentRow = {
  id: string;
  method: PosPayMethod;
  amount: number;
  reference: string;
  /** ສະກຸນຂອງຈຳນວນໃນຊ່ອງນີ້ */
  currency: ShopCurrency;
};

/** ລວມຊ່ອງຊຳລະໃນສະກຸນບິນ — NaN ຖ້າຂາດອັດຕາແລກ */
function sumPaymentsInInvoice(
  rows: PaymentRow[],
  invoiceCurrency: ShopCurrency,
  rateMap: ReadonlyMap<string, number>,
): number {
  let s = 0;
  for (const r of rows) {
    const amt = Number.isFinite(r.amount) ? r.amount : 0;
    if (amt <= 0) continue;
    const conv =
      r.currency === invoiceCurrency
        ? { convertedAmount: amt }
        : convertShopCurrencyAmount(amt, r.currency, invoiceCurrency, rateMap);
    if (conv == null) return Number.NaN;
    s += conv.convertedAmount;
  }
  return round2(s);
}

function newPaymentRowId(): string {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

type PaymentBalanceUi =
  | { kind: "match" }
  | { kind: "short"; title: string; lines: string[] }
  | { kind: "over"; title: string; lines: string[] };

/** ຍອດຄ້າງ / ເກີນໃນສະກຸນບິນ ແລະ ປະມານໃນສະກຸນຊ່ອງຊຳລະອື່ນ */
function getPaymentBalanceUi(
  invoiceCurrency: ShopCurrency,
  grandTotalInInvoice: number,
  paymentsSumInInvoice: number,
  paymentRows: PaymentRow[],
  rateMap: ReadonlyMap<string, number>,
): PaymentBalanceUi {
  if (
    !Number.isFinite(paymentsSumInInvoice) ||
    !Number.isFinite(grandTotalInInvoice)
  ) {
    return { kind: "match" };
  }
  if (
    posPaymentTotalsMatchInvoice(
      paymentsSumInInvoice,
      grandTotalInInvoice,
      invoiceCurrency,
    )
  ) {
    return { kind: "match" };
  }
  const delta = round2(paymentsSumInInvoice - grandTotalInInvoice);
  const related = new Set<ShopCurrency>([
    invoiceCurrency,
    ...paymentRows.map((r) => r.currency),
  ]);
  const currencyOrder: ShopCurrency[] = [
    invoiceCurrency,
    ...SHOP_CURRENCIES.filter((c) => c !== invoiceCurrency && related.has(c)),
  ];

  const linesForAmount = (
    amountInInvoice: number,
    conservativePaymentHints: boolean,
  ): string[] => {
    const lines: string[] = [];
    for (const c of currencyOrder) {
      if (c === invoiceCurrency) {
        lines.push(`ສະກຸນບິນ: ${formatPrice(amountInInvoice, invoiceCurrency)}`);
        continue;
      }
      if (conservativePaymentHints) {
        const suggested = suggestPaymentAmountNotOverInvoice(
          amountInInvoice,
          invoiceCurrency,
          c,
          rateMap,
        );
        if (suggested != null && suggested > 0) {
          lines.push(`≈ ${formatPrice(suggested, c)}`);
        }
      } else {
        const conv = convertShopCurrencyAmount(
          amountInInvoice,
          invoiceCurrency,
          c,
          rateMap,
        );
        if (conv != null) {
          lines.push(`≈ ${formatPrice(conv.convertedAmount, c)}`);
        }
      }
    }
    return lines;
  };

  if (delta < 0) {
    const owe = round2(-delta);
    return {
      kind: "short",
      title: "ເຫຼືອຊຳລະອີກ",
      lines: linesForAmount(owe, true),
    };
  }
  return {
    kind: "over",
    title: "ເກີນຍອດຕ້ອງຊຳລະ",
    lines: linesForAmount(delta, false),
  };
}

/** ປັບຄວາມກວ້າງ dialog ທີ່ນີ້ (Tailwind) — ຕົວຢ່າງ: lg:max-w-5xl, max-w-[52rem] */
const checkoutDialogWidthClassName =
  "w-[calc(100vw-1.5rem)] max-w-[min(56rem,calc(100vw-1.5rem))] sm:max-w-2xl md:max-w-3xl lg:max-w-4xl";

type ShopPosCheckoutModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shopId: string;
  branchId: string;
  cart: ShopPosCartLine[];
  /** ສະກຸນບິນ (MC4) */
  invoiceCurrency: ShopCurrency;
  /** ອັດຕາທີ່ມີຜົນ — ຄືນ Map ວ່າງໄດ້ຖ້າຍັງບໍ່ໂຫຼດ */
  rateMap: ReadonlyMap<string, number>;
  /** ມີຫຼາຍສະກຸນໃນກະຕ່າ — ໃຊ້ສະແດງຄຳເຕືອນອັດຕາ */
  hasMixedCurrencyCart: boolean;
  exchangeRatesLoading: boolean;
  /** ວັນເວລາອັດຕາທີ່ໂຫຼດ (ISO) — ສະແດງໃຕ້ຊ່ອງຊຳລະ */
  exchangeRatesAsOf?: string | null;
  /** ສ່ວນຫຼຸດທັງບິນຈາກໜ້າຫຼັກ (ກ່ອນເປີດ modal ອາດປັບໄດ້) */
  billDiscountDraft: number;
  billDiscountPercentDraft?: number;
  /** true = ສ່ວນຫຼຸດບິນໃນໂຫມດ % (ລວມຕອນເປັນ 0%) */
  billDiscountUsePercentDraft?: boolean;
  onCheckoutSuccess: () => void;
  /** ໂໝດງ່າຍ — ຫຼຸດຂັ້ນຕອນ ແລະ ເຊື່ອງຟີເຈີເທັກນິກ */
  simpleMode?: boolean;
};

export function ShopPosCheckoutModal({
  open,
  onOpenChange,
  shopId,
  branchId,
  cart,
  invoiceCurrency,
  rateMap,
  hasMixedCurrencyCart,
  exchangeRatesLoading,
  exchangeRatesAsOf,
  billDiscountDraft,
  billDiscountPercentDraft,
  billDiscountUsePercentDraft,
  onCheckoutSuccess,
  simpleMode = false,
}: ShopPosCheckoutModalProps) {
  const checkout = usePosCheckoutMutation(shopId, branchId);
  const { currentShop } = useMerchantShops(shopId);
  const [step, setStep] = useState<"pay" | "done">("pay");
  const [idempotencyKey, setIdempotencyKey] = useState("");
  const [paymentRows, setPaymentRows] = useState<PaymentRow[]>(() => [
    {
      id: newPaymentRowId(),
      method: "cash",
      amount: 0,
      reference: "",
      currency: "LAK",
    },
  ]);
  const [saleNumber, setSaleNumber] = useState<string | null>(null);
  const [saleId, setSaleId] = useState<string | null>(null);
  const [billDiscount, setBillDiscount] = useState(0);
  const [billDiscountPercent, setBillDiscountPercent] = useState(0);
  const [billDiscountUsePercent, setBillDiscountUsePercent] = useState(false);
  const [invBuyerName, setInvBuyerName] = useState("");
  const [invBuyerTaxId, setInvBuyerTaxId] = useState("");
  const [invBuyerAddress, setInvBuyerAddress] = useState("");
  const [taxPercent, setTaxPercent] = useState(0);
  /** ຂໍ້ຄວາມດຣາຟຊ່ອງຈຳນວນເງິນຂະນະໂຟກັດ — ກັນລຶບ .0 ຕອນກົດ */
  const [payAmountDraftByRowId, setPayAmountDraftByRowId] = useState<
    Record<string, string>
  >({});
  /** true = ບັນທຶກມັດຈຳ (ບິນ partial) */
  const [partialPaymentMode, setPartialPaymentMode] = useState(false);
  const [checkoutOutcome, setCheckoutOutcome] = useState<{
    saleStatus: "paid" | "partial";
    balanceDue: number;
    paidInInvoice: number;
  } | null>(null);

  const cartSubtotal = useMemo(
    () => cartNetSubtotalInInvoiceCurrency(cart, invoiceCurrency, rateMap),
    [cart, invoiceCurrency, rateMap],
  );
  const cartSubtotalFinite = Number.isFinite(cartSubtotal);

  const clientNetBeforeTax = useMemo(() => {
    if (!cartSubtotalFinite) return 0;
    const rawBill = billDiscountUsePercent
      ? billDiscountPercent > 0
        ? (cartSubtotal * billDiscountPercent) / 100
        : 0
      : billDiscount;
    const bd = Math.min(Math.max(0, rawBill), cartSubtotal);
    return Math.max(0, cartSubtotal - bd);
  }, [
    billDiscount,
    billDiscountPercent,
    billDiscountUsePercent,
    cartSubtotal,
    cartSubtotalFinite,
  ]);

  const { grandTotal: clientGrandTotal, taxTotal: clientTaxLineAmount } =
    useMemo(
      () => applyExclusivePosTax(clientNetBeforeTax, taxPercent),
      [clientNetBeforeTax, taxPercent],
    );

  const previewBody = useMemo((): PosCheckoutPreviewDTO | null => {
    if (!open || step !== "pay" || cart.length === 0) return null;
    const safeSub = cartSubtotalFinite ? cartSubtotal : 0;
    const billMoney = Math.min(Math.max(0, billDiscount), safeSub);
    const base: PosCheckoutPreviewDTO = {
      lines: buildPosCheckoutLinesFromCart(cart),
      invoiceCurrency,
      taxPercent: round2(Math.min(Math.max(0, taxPercent), 100)),
    };
    if (billDiscountUsePercent && billDiscountPercent > 0) {
      return { ...base, billDiscountPercent: round2(billDiscountPercent) };
    }
    if (!billDiscountUsePercent && billMoney > 0) {
      return { ...base, billDiscountAmount: round2(billMoney) };
    }
    return base;
  }, [
    open,
    step,
    cart,
    cartSubtotal,
    cartSubtotalFinite,
    billDiscount,
    billDiscountPercent,
    billDiscountUsePercent,
    invoiceCurrency,
    taxPercent,
  ]);

  const checkoutPreview = usePosCheckoutPreviewQuery(
    shopId,
    branchId,
    previewBody,
    {
      enabled: open && step === "pay",
    },
  );

  const pv = checkoutPreview.data;
  const isPreviewing = Boolean(previewBody) && checkoutPreview.isFetching;
  const totalsUsable =
    (checkoutPreview.isSuccess && pv != null) ||
    (!isPreviewing && cartSubtotalFinite);

  const netBeforeTax = pv != null ? pv.netBeforeTax : clientNetBeforeTax;
  const taxLineAmount = pv != null ? pv.taxTotal : clientTaxLineAmount;
  const grandTotal = pv != null ? pv.grandTotal : clientGrandTotal;
  const taxPercentShown = pv != null ? pv.taxPercentApplied : taxPercent;

  useEffect(() => {
    if (!open) return;
    setTaxPercent(
      Math.min(Math.max(0, Number(currentShop?.defaultTaxPercent) || 0), 100),
    );
    setPayAmountDraftByRowId({});
    setPaymentRows([
      {
        id: newPaymentRowId(),
        method: "cash",
        amount: 0,
        reference: "",
        currency: invoiceCurrency,
      },
    ]);
  }, [open, currentShop?.defaultTaxPercent, invoiceCurrency]);

  useEffect(() => {
    if (!open) return;
    setStep("pay");
    setIdempotencyKey(
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    );
    setSaleNumber(null);
    setSaleId(null);
    const safeSub = cartSubtotalFinite ? cartSubtotal : 0;
    const bd = Math.min(Math.max(0, billDiscountDraft), safeSub);
    const bp = Math.min(Math.max(0, billDiscountPercentDraft ?? 0), 100);
    const usePct =
      billDiscountUsePercentDraft !== undefined
        ? billDiscountUsePercentDraft
        : bp > 0;
    setBillDiscount(bd);
    setBillDiscountPercent(bp);
    setBillDiscountUsePercent(usePct);
    setInvBuyerName("");
    setInvBuyerTaxId("");
    setInvBuyerAddress("");
    setPartialPaymentMode(false);
    setCheckoutOutcome(null);
  }, [
    open,
    billDiscountDraft,
    billDiscountPercentDraft,
    billDiscountUsePercentDraft,
    cartSubtotal,
    cartSubtotalFinite,
  ]);

  /** ບັນທັດດຽວ — sync ຍອດກັບຍອດຊຳລະທັງໝົດເມື່ອປ່ຽນສ່ວນຫຼຸດ/ກະຕ່າ (ສະເພາະສະກຸນຊຳລະ = ສະກຸນບິນ) */
  useEffect(() => {
    if (!open || partialPaymentMode) return;
    setPaymentRows((prev) => {
      if (prev.length !== 1) return prev;
      const row = prev[0];
      if (!row) return prev;
      if (row.currency !== invoiceCurrency) return prev;
      return [
        {
          id: row.id,
          method: row.method,
          reference: row.reference,
          currency: row.currency,
          amount: grandTotal,
        },
      ];
    });
  }, [grandTotal, open, invoiceCurrency, partialPaymentMode]);

  const paymentRowsEffective = useMemo(
    () =>
      paymentRows.map((r) => {
        const draft = payAmountDraftByRowId[r.id];
        return {
          ...r,
          amount: draft !== undefined ? parseMoneyInput(draft) : r.amount,
        };
      }),
    [paymentRows, payAmountDraftByRowId],
  );

  const paymentsSumInInvoice = useMemo(
    () => sumPaymentsInInvoice(paymentRowsEffective, invoiceCurrency, rateMap),
    [paymentRowsEffective, invoiceCurrency, rateMap],
  );
  const paymentsSumFinite = Number.isFinite(paymentsSumInInvoice);

  const paymentDelta = round2(paymentsSumInInvoice - grandTotal);
  const paymentsMatch =
    paymentsSumFinite &&
    posPaymentTotalsMatchInvoice(
      paymentsSumInInvoice,
      grandTotal,
      invoiceCurrency,
    );

  const paymentsCanCheckout =
    paymentsSumFinite &&
    posPaymentMeetsOrExceedsInvoice(
      paymentsSumInInvoice,
      grandTotal,
      invoiceCurrency,
    );

  const partialDepositValid =
    paymentsSumFinite &&
    posPaymentIsStrictUnderpayment(
      paymentsSumInInvoice,
      grandTotal,
      invoiceCurrency,
    );

  const paymentsAllowSubmit = partialPaymentMode
    ? partialDepositValid
    : paymentsCanCheckout;

  const paymentBalanceUi = useMemo(
    () =>
      getPaymentBalanceUi(
        invoiceCurrency,
        grandTotal,
        paymentsSumInInvoice,
        paymentRowsEffective,
        rateMap,
      ),
    [
      grandTotal,
      invoiceCurrency,
      paymentRowsEffective,
      paymentsSumInInvoice,
      rateMap,
    ],
  );

  const paymentStatusUi = useMemo(() => {
    if (!paymentsSumFinite) {
      return {
        boxClass:
          "border-amber-500/35 bg-amber-500/5 text-amber-950 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100",
        icon: "rates" as const,
        title: "ກວດສະກຸນ ແລະ ອັດຕາແລກ",
        showExtraLines: false,
      };
    }
    if (partialPaymentMode) {
      if (partialDepositValid) {
        return {
          boxClass:
            "border-emerald-500/30 bg-emerald-500/5 text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200",
          icon: "ok" as const,
          title: "ມັດຈຳຖືກຕ້ອງ — ກົດຢືນຢັນເພື່ອບັນທຶກບິນຄ້າງຊຳລະ",
          showExtraLines: false,
        };
      }
      return {
        boxClass: "border-destructive/30 bg-destructive/5 text-destructive",
        icon: "bad" as const,
        title: "ໂໝດມັດຈຳ: ຕ້ອງຊຳລະໃຫ້ຕ່ຳກວ່າຍອດເຕັມ (ບໍ່ເຖິງເກນປິດບິນ)",
        showExtraLines:
          paymentBalanceUi.kind === "short" || paymentBalanceUi.kind === "over",
      };
    }
    if (paymentsMatch) {
      return {
        boxClass:
          "border-emerald-500/30 bg-emerald-500/5 text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200",
        icon: "ok" as const,
        title: "ຍອດຊຳລະຄົບຖ້ວນ",
        showExtraLines: false,
      };
    }
    if (paymentsCanCheckout) {
      return {
        boxClass:
          "border-amber-500/35 bg-amber-500/5 text-amber-950 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100",
        icon: "warn" as const,
        title: "ຊຳລະເກີນຍອດຕ້ອງຊຳລະ — ກົດຢືນຢັນໄດ້",
        showExtraLines:
          paymentBalanceUi.kind === "short" ||
          (paymentBalanceUi.kind === "over" && paymentsCanCheckout),
      };
    }
    return {
      boxClass: "border-destructive/30 bg-destructive/5 text-destructive",
      icon: "bad" as const,
      title:
        paymentBalanceUi.kind !== "match"
          ? paymentBalanceUi.title
          : "ຍອດຊຳລະຍັງບໍ່ຕົງກັນ",
      showExtraLines:
        paymentBalanceUi.kind === "short" ||
        (paymentBalanceUi.kind === "over" && paymentsCanCheckout),
    };
  }, [
    partialDepositValid,
    partialPaymentMode,
    paymentBalanceUi.kind,
    paymentBalanceUi.title,
    paymentsCanCheckout,
    paymentsMatch,
    paymentsSumFinite,
  ]);

  const handlePay = async () => {
    if (!idempotencyKey) return;
    if (!totalsUsable) {
      toast.error(
        isPreviewing
          ? "ລໍຖ້າຄິດຍອດຕົວຢ່າງ…"
          : "ບໍ່ສາມາດຄິດຍອດໃນສະກຸນບິນ — ກວດອັດຕາແລກໃນຕັ້ງຄ່າຮ້ານ ຫຼື ລໍຖ້າໂຫຼດອັດຕາ",
      );
      return;
    }
    if (!paymentsSumFinite) {
      toast.error("ບໍ່ສາມາດຄິດຍອດຊຳລະ — ກວດສະກຸນ ແລະ ອັດຕາແລກຄູ່ສະກຸນ");
      return;
    }
    if (!paymentsAllowSubmit) {
      if (partialPaymentMode) {
        toast.error(
          "ໂໝດມັດຈຳ: ຕ້ອງຊຳລະໃຫ້ຕ່ຳກວ່າຍອດເຕັມຕາມກົດລະບົບ (ບໍ່ຮອດຍອດຄົບທີ່ນັບວ່າປິດບິນ)",
        );
        return;
      }
      if (paymentBalanceUi.kind === "short") {
        toast.error(paymentBalanceUi.title, {
          description: paymentBalanceUi.lines.join("\n"),
        });
      } else {
        toast.error("ຍອດຊຳລະລວມຕ້ອງເທົ່າກັບຍອດຊຳລະທັງໝົດ");
      }
      return;
    }
    for (const r of paymentRows) {
      if (r.method === "transfer" && !r.reference.trim()) {
        toast.error("ໂອນຕ້ອງໃສ່ເລກອ້າງອີງໃນທຸກບັນທັດໂອນ");
        return;
      }
    }
    try {
      const bill = billDiscountUsePercent
        ? billDiscountPercent > 0
          ? Math.min((cartSubtotal * billDiscountPercent) / 100, cartSubtotal)
          : 0
        : Math.min(Math.max(0, billDiscount), cartSubtotal);
      const inv =
        invBuyerName.trim() || invBuyerTaxId.trim() || invBuyerAddress.trim()
          ? {
              buyerName: invBuyerName.trim() || undefined,
              buyerTaxId: invBuyerTaxId.trim() || undefined,
              buyerAddress: invBuyerAddress.trim() || undefined,
            }
          : undefined;
      const body = {
        lines: buildPosCheckoutLinesFromCart(cart),
        payments: paymentRowsEffective.map((r) => ({
          method: r.method,
          amount: round2(r.amount),
          ...(r.currency !== invoiceCurrency ? { currency: r.currency } : {}),
          reference:
            r.method === "transfer" && r.reference.trim()
              ? r.reference.trim()
              : undefined,
        })),
        invoiceCurrency,
        ...(bill > 0
          ? billDiscountUsePercent && billDiscountPercent > 0
            ? { billDiscountPercent: round2(billDiscountPercent) }
            : { billDiscountAmount: round2(bill) }
          : {}),
        ...(inv ? { invoice: inv } : {}),
        taxPercent: round2(Math.min(Math.max(0, taxPercent), 100)),
        ...(partialPaymentMode ? { partialPayment: true } : {}),
      };
      const res = await checkout.mutateAsync({
        body,
        idempotencyKey,
      });
      setSaleNumber(res.saleNumber);
      setSaleId(res.saleId);
      setCheckoutOutcome({
        saleStatus: res.saleStatus,
        balanceDue: res.balanceDue,
        paidInInvoice: res.paidInInvoice,
      });
      setStep("done");
      if (res.idempotentReplay) {
        toast.success("ຄຳຮ້ອງຊ້ຳ — ສະແດງບິນເດີມ");
      } else if (res.saleStatus === "partial") {
        toast.success("ບັນທຶກມັດຈຳແລ້ວ — ຊຳລະສ່ວນທີ່ເຫຼືອໃນປະຫວັດບິນ");
      }
    } catch (e) {
      toast.error(posToastError(e));
    }
  };

  const closeSuccess = () => {
    onCheckoutSuccess();
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && step === "done") {
          closeSuccess();
          return;
        }
        onOpenChange(o);
      }}
    >
      <DialogContent
        showCloseButton
        className={cn(
          "flex max-h-[min(92dvh,calc(100dvh-1rem))] flex-col overflow-x-hidden overscroll-contain",
          step === "done"
            ? "max-w-md gap-0 overflow-y-auto p-0 sm:max-w-md"
            : cn("gap-4 overflow-y-auto", checkoutDialogWidthClassName),
        )}
      >
        {step === "done" ? (
          <DialogHeader className="sr-only">
            <DialogTitle>
              {checkoutOutcome?.saleStatus === "partial"
                ? "ບັນທຶກມັດຈຳແລ້ວ"
                : "ຊຳລະສຳເລັດ"}
            </DialogTitle>
            <DialogDescription>ຊຳລະສຳເລັດແລ້ວ</DialogDescription>
          </DialogHeader>
        ) : (
          <DialogHeader>
            <DialogTitle>ຊຳລະເງິນ</DialogTitle>
            <DialogDescription>
              {`${invoiceCurrency} · ກວດສອບຍອດກ່ອນຢືນຢັນ`}
              {hasMixedCurrencyCart && exchangeRatesLoading ? (
                <span className="mt-1 block text-muted-foreground text-xs">
                  ກຳລັງໂຫຼດອັດຕາແລກ…
                </span>
              ) : null}
              {hasMixedCurrencyCart &&
              !exchangeRatesLoading &&
              !cartSubtotalFinite ? (
                <span className="mt-1 block text-amber-700 text-xs dark:text-amber-300">
                  ບໍ່ມີອັດຕາຄູ່ສະກຸນ —{" "}
                  <Link
                    to="/app/shops/$shopId/settings"
                    params={{ shopId }}
                    className="font-medium text-primary underline underline-offset-2"
                  >
                    ຕັ້ງຄ່າຮ້ານ
                  </Link>{" "}
                  (ອັດຕາແລກ) ກ່ອນຊຳລະ
                </span>
              ) : null}
            </DialogDescription>
          </DialogHeader>
        )}

        {step === "done" && saleNumber && saleId && checkoutOutcome ? (
          <ShopPosCheckoutSuccessPanel
            shopId={shopId}
            saleId={saleId}
            saleNumber={saleNumber}
            invoiceCurrency={invoiceCurrency}
            paidAmount={checkoutOutcome.paidInInvoice}
            checkoutOutcome={checkoutOutcome}
            onDone={closeSuccess}
          />
        ) : step === "done" ? (
          <div className="flex justify-center py-16">
            <Loader />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:items-start md:gap-6 lg:gap-8">
            {/* ຄໍລຳຊ້າຍ: ສະຫຼຸບຍອດ + ໃບກຳກັບ */}
            <div className="flex min-w-0 flex-col gap-5 md:sticky md:top-1 md:z-10 md:self-start">
              <section
                aria-labelledby="pos-checkout-summary-heading"
                className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm"
              >
                <div className="flex items-center gap-2 border-border border-b bg-muted/40 px-4 py-3">
                  <Receipt
                    className="size-4 shrink-0 text-[hsl(210_90%_48%)]"
                    aria-hidden
                  />
                  <h2
                    id="pos-checkout-summary-heading"
                    className="font-heading font-semibold text-foreground text-sm"
                  >
                    ສະຫຼຸບຍອດບິນ
                  </h2>
                </div>
                <div className="space-y-3 px-4 py-4 text-sm">
                  {isPreviewing ? (
                    <p className="text-muted-foreground text-xs">
                      ກຳລັງຄິດຕົວຢ່າງສ່ວນຫຼຸດໂປຣ / ຍອດຈາກລະບົບ…
                    </p>
                  ) : null}
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-muted-foreground">
                      {pv != null ? "ລວມສິນຄ້າ (ກ່ອນສ່ວນຫຼຸດ)" : "ລວມລາຍການ"}
                    </span>
                    <span className="font-medium text-foreground tabular-nums">
                      {pv != null
                        ? formatPrice(pv.grossSubtotal, invoiceCurrency)
                        : cartSubtotalFinite
                          ? formatPrice(cartSubtotal, invoiceCurrency)
                          : "—"}
                    </span>
                  </div>
                  {pv != null && pv.promotionDiscountTotal > 0.009 ? (
                    <div className="flex items-center justify-between gap-4 text-emerald-800 dark:text-emerald-200">
                      <span className="text-muted-foreground">
                        ສ່ວນຫຼຸດໂປຣ (ອັດຕະໂນມັດ)
                      </span>
                      <span className="font-medium tabular-nums">
                        −
                        {formatPrice(
                          pv.promotionDiscountTotal,
                          invoiceCurrency,
                        )}
                      </span>
                    </div>
                  ) : null}
                  {pv != null && pv.manualLineDiscountTotal > 0.009 ? (
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-muted-foreground">
                        ສ່ວນຫຼຸດຕໍ່ລາຍການ (ມື)
                      </span>
                      <span className="font-medium tabular-nums">
                        −
                        {formatPrice(
                          pv.manualLineDiscountTotal,
                          invoiceCurrency,
                        )}
                      </span>
                    </div>
                  ) : null}
                  {pv != null && pv.billDiscountTotal > 0.009 ? (
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-muted-foreground">
                        ສ່ວນຫຼຸດທ້າຍບິນ (ຫຼັງຄຳນວນແລ້ວ)
                      </span>
                      <span className="font-medium tabular-nums">
                        −{formatPrice(pv.billDiscountTotal, invoiceCurrency)}
                      </span>
                    </div>
                  ) : null}
                  {!simpleMode ? (
                    <div>
                      <label
                        htmlFor="pos-bill-discount"
                        className="mb-1.5 block font-medium text-card-foreground text-xs"
                      >
                        ສ່ວນຫຼຸດທັງບິນ
                      </label>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant={
                            !billDiscountUsePercent ? "default" : "outline"
                          }
                          className="h-10 min-w-14 text-xs"
                          onClick={() => {
                            setBillDiscountUsePercent(false);
                            setBillDiscountPercent(0);
                          }}
                        >
                          ເງິນ
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant={
                            billDiscountUsePercent ? "default" : "outline"
                          }
                          className="h-10 min-w-14 text-xs"
                          onClick={() => {
                            setBillDiscountUsePercent(true);
                            const next =
                              billDiscount > 0 &&
                              cartSubtotalFinite &&
                              cartSubtotal > 0
                                ? Math.min(
                                    (billDiscount / cartSubtotal) * 100,
                                    100,
                                  )
                                : 0;
                            setBillDiscountPercent(round2(next));
                          }}
                        >
                          %
                        </Button>
                        {billDiscountUsePercent ? (
                          <Input
                            id="pos-bill-discount"
                            type="number"
                            inputMode="decimal"
                            min={0}
                            max={100}
                            step={0.01}
                            autoComplete="off"
                            value={billDiscountPercent}
                            onChange={(e) => {
                              const n = Number(e.target.value);
                              setBillDiscountPercent(
                                Number.isFinite(n)
                                  ? Math.min(Math.max(0, n), 100)
                                  : 0,
                              );
                            }}
                            className="h-10 w-28 font-mono text-sm"
                            placeholder="0"
                          />
                        ) : (
                          <Input
                            id="pos-bill-discount"
                            type="text"
                            inputMode="decimal"
                            autoComplete="off"
                            value={formatMoneyInputDisplay(billDiscount)}
                            onChange={(e) => {
                              const raw = parseMoneyInput(e.target.value);
                              setBillDiscount(
                                Math.min(
                                  raw,
                                  cartSubtotalFinite ? cartSubtotal : 0,
                                ),
                              );
                            }}
                            className="h-10 font-mono text-sm"
                            placeholder="0"
                          />
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
                {!simpleMode ? (
                  <div className="border-border border-t px-4 py-3">
                    <label
                      htmlFor="pos-checkout-tax-percent"
                      className="mb-1.5 block font-medium text-card-foreground text-xs"
                    >
                      VAT (%)
                    </label>
                    <Input
                      id="pos-checkout-tax-percent"
                      type="number"
                      inputMode="decimal"
                      min={0}
                      max={100}
                      step={0.01}
                      autoComplete="off"
                      value={Number.isFinite(taxPercent) ? taxPercent : 0}
                      onChange={(e) => {
                        const n = Number(e.target.value);
                        setTaxPercent(
                          Number.isFinite(n)
                            ? Math.min(Math.max(0, n), 100)
                            : 0,
                        );
                      }}
                      className="h-10 w-full max-w-[10rem] font-mono text-sm"
                      placeholder="0"
                    />
                    <p className="mt-1.5 text-[11px] text-muted-foreground leading-snug">
                      ຄິດເທິງຍອດຫຼັງຫຼຸດ — ຄ່າເລີ່ມຕົ້ນຈາກຮ້ານ (ປັບໄດ້ຕໍ່ບິນ)
                    </p>
                  </div>
                ) : null}
                <div className="border-primary/25 border-t bg-primary/6 px-4 py-4">
                  {taxPercentShown > 0.0001 || taxLineAmount > 0.009 ? (
                    <div className="mb-3 space-y-1.5 text-sm">
                      <div className="flex justify-between gap-3">
                        <span className="text-muted-foreground">ຍອດກ່ອນVAT</span>
                        <span className="tabular-nums">
                          {formatPrice(netBeforeTax, invoiceCurrency)}
                        </span>
                      </div>
                      <div className="flex justify-between gap-3">
                        <span className="text-muted-foreground">
                          VAT ({formatPosTaxPercentLabel(taxPercentShown)}%)
                        </span>
                        <span className="tabular-nums">
                          {formatPrice(taxLineAmount, invoiceCurrency)}
                        </span>
                      </div>
                    </div>
                  ) : null}
                  <div className="flex items-end justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <CircleDollarSign
                        className="size-5 shrink-0 text-primary"
                        aria-hidden
                      />
                      <span className="font-heading font-semibold text-foreground text-sm uppercase tracking-wide">
                        ຕ້ອງຊຳລະ
                      </span>
                    </div>
                    <span className="font-bold font-heading text-2xl text-primary tabular-nums leading-none">
                      {formatPrice(grandTotal, invoiceCurrency)}
                    </span>
                  </div>
                  {simpleMode ? (
                    <p className="mt-2 text-muted-foreground text-xs">
                      ເລືອກວິທີຊຳລະຂ້າງລຸ່ມ ແລ້ວກົດຢືນຢັນ
                    </p>
                  ) : (
                    <>
                      <p className="mt-2 text-muted-foreground text-xs md:hidden">
                        ກຳນົດຊ່ອງຊຳລະລຸ່ມນີ້ໃຫ້ລວມເທົ່າກັບຍອດນີ້
                      </p>
                      <p className="mt-2 hidden text-muted-foreground text-xs md:block">
                        ກຳນົດຊ່ອງຊຳລະຄໍລຳຂວາໃຫ້ລວມເທົ່າກັບຍອດນີ້
                      </p>
                    </>
                  )}
                </div>
              </section>

              {!simpleMode ? (
                <section
                  aria-labelledby="pos-checkout-invoice-heading"
                  className="rounded-xl border border-border bg-card/50 px-4 py-3 shadow-sm"
                >
                  <h2
                    id="pos-checkout-invoice-heading"
                    className="mb-2 font-heading font-semibold text-foreground text-xs"
                  >
                    ໃບກຳກັບພາສີ (ທາງເລືອກ)
                  </h2>
                  {/* <p className="mb-3 text-muted-foreground text-xs">
                  ບັນທຶກ snapshot ຕອນຊຳລະ — ສຳລັບພິມໃບກຳກັບ · VAT ຕາມຊ່ອງຊ້າຍ (
                  {formatPosTaxPercentLabel(taxPercent) || "0"}%)
                </p> */}
                  <div className="space-y-2">
                    <Input
                      value={invBuyerName}
                      onChange={(e) => setInvBuyerName(e.target.value)}
                      placeholder="ຊື່ຜູ້ຊື້ / ນິຕິບຸກຄົນ"
                      className="h-9 text-sm"
                    />
                    <Input
                      value={invBuyerTaxId}
                      onChange={(e) => setInvBuyerTaxId(e.target.value)}
                      placeholder="ເລກປະຈຳຕົວຜູ້ເສຍVATຜູ້ຊື້"
                      className="h-9 font-mono text-sm"
                    />
                    <textarea
                      value={invBuyerAddress}
                      onChange={(e) => setInvBuyerAddress(e.target.value)}
                      placeholder="ທີ່ຢູ່ຜູ້ຊື້"
                      rows={2}
                      className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  </div>
                </section>
              ) : null}
            </div>

            {/* ຄໍລຳຂວາ: ຊ່ອງຊຳລະ */}
            <div className="min-w-0 space-y-5 md:min-h-0">
              {simpleMode && paymentRows[0] ? (
                <section aria-labelledby="pos-checkout-simple-pay-heading">
                  <h2
                    id="pos-checkout-simple-pay-heading"
                    className="mb-3 font-heading font-semibold text-foreground text-base"
                  >
                    ເລືອກວິທີຊຳລະ
                  </h2>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    {(Object.keys(METHOD_CONFIG) as PosPayMethod[]).map((k) => {
                      const { label, Icon } = METHOD_CONFIG[k];
                      const selected = paymentRows[0]?.method === k;
                      return (
                        <button
                          key={k}
                          type="button"
                          onClick={() =>
                            setPaymentRows((rows) =>
                              rows.map((r, i) =>
                                i === 0 ? { ...r, method: k } : r,
                              ),
                            )
                          }
                          className={cn(
                            "flex min-h-[5.5rem] flex-col items-center justify-center gap-2 rounded-2xl border-2 px-4 py-4 text-sm transition-all touch-manipulation",
                            selected
                              ? "border-primary bg-primary/10 text-primary shadow-md"
                              : "border-border bg-background text-foreground hover:border-primary/40 hover:bg-muted/50",
                          )}
                        >
                          <Icon
                            className={cn(
                              "size-8",
                              selected
                                ? "text-primary"
                                : "text-muted-foreground",
                            )}
                            aria-hidden
                          />
                          <span className="font-bold text-base">{label}</span>
                        </button>
                      );
                    })}
                  </div>
                </section>
              ) : null}
              <section aria-labelledby="pos-checkout-payments-heading">
                <div className="mb-3 space-y-1">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Wallet
                        className="size-4 shrink-0 text-[hsl(210_90%_48%)]"
                        aria-hidden
                      />
                      <h2
                        id="pos-checkout-payments-heading"
                        className="font-heading font-semibold text-foreground text-sm"
                      >
                        ຊ່ອງຊຳລະ
                      </h2>
                      <span className="rounded-md bg-muted px-2 py-0.5 text-muted-foreground text-xs">
                        {paymentRows.length} ຊ່ອງ
                      </span>
                    </div>
                    {!simpleMode ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-9 gap-1.5 border-dashed text-xs"
                        disabled={paymentRows.length >= 20}
                        onClick={() =>
                          setPaymentRows((rows) => [
                            ...rows,
                            {
                              id: newPaymentRowId(),
                              method: "cash",
                              amount: 0,
                              reference: "",
                              currency: invoiceCurrency,
                            },
                          ])
                        }
                      >
                        <Plus className="size-3.5" aria-hidden />
                        ເພີ່ມຊ່ອງ
                      </Button>
                    ) : null}
                  </div>
                  {exchangeRatesAsOf ? (
                    <p className="text-[10px] text-muted-foreground">
                      ອັດຕາແລກ (ອ້າງອີງ):{" "}
                      {new Date(exchangeRatesAsOf).toLocaleString("lo-LA")}
                    </p>
                  ) : null}
                </div>

                <div className="space-y-3">
                  {paymentRows.map((row, idx) => {
                    const payAmountDraft = payAmountDraftByRowId[row.id];
                    return (
                      <div
                        key={row.id}
                        className="rounded-xl border border-border bg-card/80 p-4 shadow-sm ring-1 ring-black/5 dark:ring-white/10"
                      >
                        <div className="mb-3 flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-muted font-heading font-semibold text-foreground text-xs">
                              {idx + 1}
                            </span>
                            <span className="text-muted-foreground text-xs">
                              ຊ່ອງຊຳລະ {idx + 1}
                            </span>
                          </div>
                          {paymentRows.length > 1 ? (
                            <button
                              type="button"
                              className="inline-flex size-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                              onClick={() => {
                                setPayAmountDraftByRowId((d) => {
                                  const { [row.id]: _, ...rest } = d;
                                  return rest;
                                });
                                setPaymentRows((rows) => {
                                  const next = rows.filter(
                                    (r) => r.id !== row.id,
                                  );
                                  if (next.length === 1 && next[0]) {
                                    const only = next[0];
                                    return [
                                      {
                                        ...only,
                                        amount:
                                          only.currency === invoiceCurrency
                                            ? grandTotal
                                            : only.amount,
                                      },
                                    ];
                                  }
                                  return next;
                                });
                              }}
                              aria-label={`ລຶບຊ່ອງຊຳລະ ${idx + 1}`}
                            >
                              <Trash2 className="size-4" />
                            </button>
                          ) : null}
                        </div>

                        <p className="mb-2 font-medium text-card-foreground text-xs">
                          ວິທີຊຳລະ
                        </p>
                        {!simpleMode ? (
                          <div className="mb-4 grid grid-cols-3 gap-2">
                            {(Object.keys(METHOD_CONFIG) as PosPayMethod[]).map(
                              (k) => {
                                const { label, Icon } = METHOD_CONFIG[k];
                                const selected = row.method === k;
                                return (
                                  <button
                                    key={k}
                                    type="button"
                                    onClick={() =>
                                      setPaymentRows((rows) =>
                                        rows.map((r) =>
                                          r.id === row.id
                                            ? { ...r, method: k }
                                            : r,
                                        ),
                                      )
                                    }
                                    className={`flex flex-col items-center gap-1.5 rounded-xl border-2 px-2 py-2.5 text-xs transition-all ${
                                      selected
                                        ? "border-primary bg-primary/10 text-primary shadow-sm"
                                        : "border-border bg-background text-muted-foreground hover:border-border hover:bg-muted/50"
                                    }`}
                                  >
                                    <Icon
                                      className={`size-5 ${selected ? "text-primary" : "text-muted-foreground"}`}
                                      aria-hidden
                                    />
                                    <span className="font-medium text-[11px] leading-tight">
                                      {label}
                                    </span>
                                  </button>
                                );
                              },
                            )}
                          </div>
                        ) : null}

                        {!simpleMode ? (
                          <div className="mb-4 space-y-1.5">
                            <Label
                              htmlFor={`pos-pay-cur-${row.id}`}
                              className="text-card-foreground text-xs"
                            >
                              ສະກຸນຊຳລະ
                            </Label>
                            <Select
                              value={row.currency}
                              onValueChange={(v) => {
                                if (!isShopCurrency(v)) return;
                                setPayAmountDraftByRowId((d) => {
                                  const { [row.id]: _, ...rest } = d;
                                  return rest;
                                });
                                setPaymentRows((rows) =>
                                  rows.map((r) =>
                                    r.id === row.id
                                      ? { ...r, currency: v as ShopCurrency }
                                      : r,
                                  ),
                                );
                              }}
                            >
                              <SelectTrigger
                                id={`pos-pay-cur-${row.id}`}
                                className="h-10 w-full max-w-[min(100%,20rem)] font-mono text-sm"
                              >
                                <SelectValue placeholder={row.currency} />
                              </SelectTrigger>
                              <SelectContent>
                                {SHOP_CURRENCIES.map((c) => {
                                  const rateLine =
                                    c === invoiceCurrency
                                      ? "ສະກຸນບິນ"
                                      : (formatPosFxRateLine(
                                          c,
                                          invoiceCurrency,
                                          rateMap,
                                        ) ?? "ບໍ່ມີອັດຕາແລກ");
                                  return (
                                    <SelectItem key={c} value={c}>
                                      <span className="flex min-w-0 max-w-[min(92vw,22rem)] items-baseline justify-between gap-3">
                                        <span className="shrink-0 font-medium font-mono">
                                          {c}
                                        </span>
                                        <span className="truncate font-normal text-[11px] text-muted-foreground">
                                          {rateLine}
                                        </span>
                                      </span>
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                            {row.currency !== invoiceCurrency ? (
                              <>
                                {(() => {
                                  const line = formatPosFxRateLine(
                                    row.currency,
                                    invoiceCurrency,
                                    rateMap,
                                  );
                                  return line ? (
                                    <p className="font-mono text-[11px] text-muted-foreground leading-snug">
                                      {line}
                                    </p>
                                  ) : null;
                                })()}
                                <p className="text-[11px] text-muted-foreground leading-snug">
                                  ຈຳນວນຂ້າງລຸ່ມເປັນ {row.currency} — ລະບົບແປງເປັນ{" "}
                                  {invoiceCurrency} ເພື່ອກວດກັບຍອດບິນ
                                </p>
                              </>
                            ) : null}
                          </div>
                        ) : null}

                        <div className="space-y-4">
                          <div>
                            <label
                              className="mb-1.5 block font-medium text-card-foreground text-xs"
                              htmlFor={`pos-pay-amt-${row.id}`}
                            >
                              {simpleMode
                                ? "ຈຳນວນເງິນທີ່ຮັບ"
                                : `ຈຳນວນເງິນ (${row.currency})`}
                            </label>
                            <Input
                              id={`pos-pay-amt-${row.id}`}
                              type="text"
                              inputMode="decimal"
                              autoComplete="off"
                              value={
                                payAmountDraft !== undefined
                                  ? payAmountDraft
                                  : formatMoneyInputDisplay(row.amount)
                              }
                              onFocus={() => {
                                setPayAmountDraftByRowId((d) => ({
                                  ...d,
                                  [row.id]: formatMoneyInputDisplay(row.amount),
                                }));
                              }}
                              onBlur={() => {
                                setPayAmountDraftByRowId((d) => {
                                  const draft = d[row.id];
                                  if (draft !== undefined) {
                                    const parsed = parseMoneyInput(draft);
                                    setPaymentRows((rows) =>
                                      rows.map((r) =>
                                        r.id === row.id
                                          ? { ...r, amount: parsed }
                                          : r,
                                      ),
                                    );
                                  }
                                  const { [row.id]: _, ...rest } = d;
                                  return rest;
                                });
                              }}
                              onChange={(e) => {
                                const next = sanitizeMoneyDecimalTyping(
                                  e.target.value,
                                );
                                setPayAmountDraftByRowId((d) => ({
                                  ...d,
                                  [row.id]: next,
                                }));
                              }}
                              className={cn(
                                "font-mono tabular-nums",
                                simpleMode ? "h-14 text-2xl" : "h-11 text-base",
                              )}
                              placeholder="0"
                            />
                          </div>
                          {row.method === "transfer" ? (
                            <div>
                              <label
                                className="mb-1.5 block font-medium text-card-foreground text-xs"
                                htmlFor={`pos-pay-ref-${row.id}`}
                              >
                                ເລກອ້າງອີງ (ບັນຊີ / ທຸລະກຳ)
                              </label>
                              <Input
                                id={`pos-pay-ref-${row.id}`}
                                value={row.reference}
                                onChange={(e) =>
                                  setPaymentRows((rows) =>
                                    rows.map((r) =>
                                      r.id === row.id
                                        ? { ...r, reference: e.target.value }
                                        : r,
                                    ),
                                  )
                                }
                                placeholder="ຕົວຢ່າງ: ref ຈາກທະນາຄານ"
                                className="h-10 font-mono text-sm"
                              />
                            </div>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <output
                  aria-live="polite"
                  className={`mt-4 flex items-start gap-3 rounded-xl border px-3 py-3 text-sm ${paymentStatusUi.boxClass}`}
                >
                  {paymentStatusUi.icon === "ok" ? (
                    <CheckCircle2
                      className="mt-0.5 size-4 shrink-0 text-emerald-600 dark:text-emerald-400"
                      aria-hidden
                    />
                  ) : paymentStatusUi.icon === "warn" ||
                    paymentStatusUi.icon === "rates" ? (
                    <CircleDollarSign
                      className="mt-0.5 size-4 shrink-0 text-amber-700 dark:text-amber-300"
                      aria-hidden
                    />
                  ) : (
                    <AlertCircle
                      className="mt-0.5 size-4 shrink-0"
                      aria-hidden
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{paymentStatusUi.title}</p>
                    <p className="mt-1 text-xs opacity-90">
                      ລວມຊຳລະ ({invoiceCurrency}):{" "}
                      <span className="font-mono tabular-nums">
                        {paymentsSumFinite
                          ? formatPrice(paymentsSumInInvoice, invoiceCurrency)
                          : "—"}
                      </span>
                      {" · "}
                      {partialPaymentMode ? (
                        <>
                          ຍອດເຕັມບິນ:{" "}
                          <span className="font-mono tabular-nums">
                            {formatPrice(grandTotal, invoiceCurrency)}
                          </span>
                          {paymentsSumFinite && partialDepositValid ? (
                            <>
                              {" · "}
                              <span className="font-medium">
                                ຄ້າງຫຼັງມັດຈຳ:{" "}
                                {formatPrice(
                                  round2(grandTotal - paymentsSumInInvoice),
                                  invoiceCurrency,
                                )}
                              </span>
                            </>
                          ) : null}
                        </>
                      ) : (
                        <>
                          ຕ້ອງຊຳລະ:{" "}
                          <span className="font-mono tabular-nums">
                            {formatPrice(grandTotal, invoiceCurrency)}
                          </span>
                          {!paymentsMatch && paymentsSumFinite ? (
                            <span className="ms-1 font-medium">
                              ({paymentDelta > 0 ? "+" : ""}
                              {formatPrice(paymentDelta, invoiceCurrency)})
                            </span>
                          ) : null}
                        </>
                      )}
                    </p>
                    {paymentStatusUi.showExtraLines ? (
                      <ul className="mt-2 space-y-0.5 text-xs opacity-90">
                        {paymentBalanceUi.lines.map((line, i) => (
                          <li
                            key={`${paymentBalanceUi.kind}-${i}-${line}`}
                            className="font-mono text-[11px] tabular-nums leading-snug"
                          >
                            {line}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                </output>
              </section>

              <div className="space-y-2 border-border border-t pt-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:justify-end sm:gap-3">
                  {!simpleMode ? (
                    <Button
                      type="button"
                      variant={partialPaymentMode ? "default" : "outline"}
                      className="h-11 w-full font-semibold sm:w-auto sm:min-w-[7.5rem]"
                      onClick={() => setPartialPaymentMode((v) => !v)}
                      aria-pressed={partialPaymentMode}
                      disabled={checkout.isPending}
                      title={
                        partialPaymentMode
                          ? "ປິດໂໝດມັດຈຳ — ຊຳລະເຕັມຕາມຍອດ"
                          : "ເປີດໂໝດມັດຈຳ — ບິນຄ້າງຊຳລະ"
                      }
                    >
                      ມັດຈຳ
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    className={cn(
                      "w-full gap-2 font-bold sm:w-auto",
                      simpleMode
                        ? "h-14 min-w-[200px] text-base"
                        : "h-11 sm:min-w-[180px] font-semibold",
                    )}
                    onClick={() => void handlePay()}
                    disabled={
                      checkout.isPending ||
                      !paymentsAllowSubmit ||
                      !totalsUsable ||
                      !paymentsSumFinite
                    }
                  >
                    {checkout.isPending ? <Loader /> : null}
                    {partialPaymentMode ? "ຢືນຢັນມັດຈຳ" : "ຢືນຢັນຊຳລະ"}
                  </Button>
                </div>
                {!simpleMode ? (
                  <p className="text-[11px] text-muted-foreground leading-snug sm:text-end">
                    ກົດ «ມັດຈຳ» ເພື່ອບັນທຶກບິນຄ້າງຊຳລະ; ກົດອີກຄັ້ງເພື່ອກັບໄປຊຳລະເຕັມຕາມຍອດ
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** ສຳລັບ POST checkout / holds — ຮ່ວມສ່ວນຫຼຸດຕໍ່ລາຍການ */
export function buildPosCheckoutLinesFromCart(cart: ShopPosCartLine[]) {
  return cart.map((l) => {
    const disc = lineDiscountApplied(l);
    const discField =
      disc > 0
        ? l.lineDiscountUsePercent === true
          ? { lineDiscountPercent: round2(l.lineDiscountPercent ?? 0) }
          : { lineDiscountAmount: round2(disc) }
        : ({} as object);
    const base = { shopProductSkuId: l.row.id, qty: l.qty, ...discField };
    if (l.serialId) {
      return {
        shopProductSkuId: l.row.id,
        qty: 1,
        serials: [{ serialId: l.serialId }],
        ...discField,
      };
    }
    if (l.serialNo && !l.serialId) {
      return {
        shopProductSkuId: l.row.id,
        qty: 1,
        serials: [{ serialNo: l.serialNo }],
        ...discField,
      };
    }
    if (l.lotId) return { ...base, lotId: l.lotId };
    if (l.lotNo) return { ...base, lotNo: l.lotNo };
    return base;
  });
}

export function cartNetSubtotal(cart: ShopPosCartLine[]): number {
  return cart.reduce(
    (sum, line) => sum + (lineGross(line) - lineDiscountApplied(line)),
    0,
  );
}
